import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import PageArea from '../../../../components/shared/PageArea';
import PageTitle from '../../../../components/shared/PageTitle';
import { Section } from '../../../../components/ui/Section';
import { TableHeader } from '../../../../components/forms/TableHeader';
import { Pill } from '../../../../components/shared/Pill';
import CustomPagination from '../../../../components/forms/Pagination';
import { UserContext } from '../../../../components/shared/useUser';
import { Plus } from '../../../../assets';
import {
  ControlTemplateItem,
  ControlTemplateStatus,
  ControlTemplateType,
  controlTemplatesSeed,
} from '../../../../mocks/data/controlTemplatesData';

const templateTypes: ControlTemplateType[] = [
  'ControlTemplate', 'ControlCategory', 'SeverityLevel', 'EscalationRule', 'EvidenceRequirement', 'ActionItemTemplate',
];

const statuses: ControlTemplateStatus[] = ['Active', 'Draft', 'Archived'];

const emptyForm: Omit<ControlTemplateItem, 'id'> = {
  type: 'ControlTemplate',
  name: '',
  description: '',
  status: 'Draft',
  controlArea: '',
  lastUpdated: new Date().toISOString().split('T')[0],
};

const ControlTemplatesPage: React.FC = () => {
  const { t } = useTranslation('complianceControls');
  const { hasPermissionTo } = useContext(UserContext);

  // Placeholder permission reuse: control templates/rules setup don't have a dedicated real
  // permission yet, so this reuses AuditTemplatesView (closest existing setup permission)
  // until a real backend permission is added.
  const canView = hasPermissionTo ? hasPermissionTo(['AuditTemplatesView']) : true;
  const permissions = {
    canRead: canView,
    canCreate: hasPermissionTo ? hasPermissionTo(['AuditTemplatesView']) : true,
    canUpdate: hasPermissionTo ? hasPermissionTo(['AuditTemplatesView']) : true,
    canDelete: hasPermissionTo ? hasPermissionTo(['AuditTemplatesView']) : true,
  };

  const [items, setItems] = useState<ControlTemplateItem[]>(controlTemplatesSeed);
  const [typeFilter, setTypeFilter] = useState<ControlTemplateType | ''>('');
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Omit<ControlTemplateItem, 'id'>>(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState<ControlTemplateItem | null>(null);

  const filteredItems = useMemo(() => {
    if (!typeFilter) return items;
    return items.filter((item) => item.type === typeFilter);
  }, [items, typeFilter]);

  const maxPages = Math.max(1, Math.ceil(filteredItems.length / rowsPerPage));
  const pagedItems = filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleOpenCreate = () => {
    if (!permissions.canCreate) return;
    setEditingId(null);
    setFormData({ ...emptyForm, type: typeFilter || 'ControlTemplate' });
    setDialogOpen(true);
  };

  const handleOpenEdit = (item: ControlTemplateItem) => {
    if (!permissions.canUpdate) return;
    setEditingId(item.id);
    const { id: _id, ...rest } = item;
    setFormData(rest);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setFormData(emptyForm);
  };

  const handleSave = () => {
    if (editingId) {
      setItems((prev) => prev.map((item) => (item.id === editingId ? { ...formData, id: editingId } : item)));
    } else {
      const newId = `CT-${Math.floor(1000 + Math.random() * 9000)}`;
      setItems((prev) => [{ ...formData, id: newId }, ...prev]);
    }
    handleCloseDialog();
  };

  const handleRequestDelete = (item: ControlTemplateItem, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!permissions.canDelete) return;
    setDeleteTarget(item);
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    setItems((prev) => prev.filter((item) => item.id !== deleteTarget.id));
    setDeleteTarget(null);
  };

  if (!permissions.canRead) {
    return (
      <PageArea>
        <PageTitle>{t('controlTemplates.title')}</PageTitle>
        <Pill text={t('noPermissionToView')} />
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Grid container
        spacing={5}>
        <Grid item
          xs={12}>
          <Grid container
            justifyContent="space-between"
            alignItems="flex-start">
            <Grid item>
              <PageTitle subTitle={t('controlTemplates.subtitle')}>
                {t('controlTemplates.title')}
              </PageTitle>
            </Grid>
            <Grid item>
              {permissions.canCreate && (
                <Button
                  size="lg"
                  variant="primary"
                  startIcon={<Plus />}
                  onClick={handleOpenCreate}
                >
                  {t('controlTemplates.createItem')}
                </Button>
              )}
            </Grid>
          </Grid>
        </Grid>

        <Grid item
          xs={12}>
          <Grid container
            spacing={1}
            alignItems="center">
            <Grid item>
              <Chip
                label={t('all')}
                color={typeFilter === '' ? 'primary' : 'default'}
                onClick={() => { setTypeFilter(''); setPage(0); }}
              />
            </Grid>
            {templateTypes.map((type) => (
              <Grid item
                key={type}>
                <Chip
                  label={t(`controlTemplates.typeValues.${type}`)}
                  color={typeFilter === type ? 'primary' : 'default'}
                  onClick={() => { setTypeFilter((prev) => (prev === type ? '' : type)); setPage(0); }}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>

        <Grid item
          xs={12}>
          <Section>
            <TableHeader>
              {t('controlTemplates.title')}
              <Pill text={`${filteredItems.length} ${t('controlTemplates.items')}`} />
            </TableHeader>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('name')}</TableCell>
                    <TableCell>{t('type')}</TableCell>
                    <TableCell>{t('description')}</TableCell>
                    <TableCell>{t('controlArea')}</TableCell>
                    <TableCell>{t('status')}</TableCell>
                    <TableCell>{t('controlTemplates.lastUpdated')}</TableCell>
                    <TableCell align="right">{t('actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedItems.map((item) => (
                    <TableRow key={item.id}
                      hover>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>
                        <Chip label={t(`controlTemplates.typeValues.${item.type}`)}
                          size="small"
                          variant="outlined" />
                      </TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell>{item.controlArea}</TableCell>
                      <TableCell>{t(`statusValues.${item.status}`)}</TableCell>
                      <TableCell>{item.lastUpdated}</TableCell>
                      <TableCell align="right">
                        {permissions.canUpdate && (
                          <Tooltip title={t('edit') as string}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEdit(item)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {permissions.canDelete && (
                          <Tooltip title={t('delete') as string}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => handleRequestDelete(item, e)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {pagedItems.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7}
                        style={{ textAlign: 'center' }}>
                        {t('controlTemplates.noResults')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <CustomPagination
              page={page}
              setPage={setPage}
              maxPages={maxPages}
              breakpointForChangeDisplay={120000}
              useNoDataMessage={false}
            />
          </Section>
        </Grid>
      </Grid>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingId ? t('controlTemplates.editItem') : t('controlTemplates.createItem')}</DialogTitle>
        <DialogContent>
          <Grid container
            spacing={3}
            sx={{ mt: 0.5 }}>
            <Grid item
              xs={12}
              sm={6}>
              <TextField
                select
                label={t('type')}
                value={formData.type}
                onChange={(e) => setFormData((prev) => ({ ...prev, type: e.target.value as ControlTemplateType }))}
                fullWidth
                size="small"
              >
                {templateTypes.map((type) => (
                  <MenuItem key={type}
                    value={type}>{t(`controlTemplates.typeValues.${type}`)}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              xs={12}
              sm={6}>
              <TextField
                select
                label={t('status')}
                value={formData.status}
                onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value as ControlTemplateStatus }))}
                fullWidth
                size="small"
              >
                {statuses.map((s) => (
                  <MenuItem key={s}
                    value={s}>{t(`statusValues.${s}`)}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              xs={12}>
              <TextField
                label={t('name')}
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item
              xs={12}>
              <TextField
                label={t('description')}
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                fullWidth
                multiline
                minRows={2}
                size="small"
              />
            </Grid>
            <Grid item
              xs={12}
              sm={6}>
              <TextField
                label={t('controlArea')}
                value={formData.controlArea}
                onChange={(e) => setFormData((prev) => ({ ...prev, controlArea: e.target.value }))}
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}
            variant="secondary"
            size="md">
            {t('cancel')}
          </Button>
          <Button onClick={handleSave}
            variant="primary"
            size="md">
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
      >
        <DialogTitle>{t('delete')}?</DialogTitle>
        <DialogContent>{t('controlTemplates.deleteItemConfirm')}</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}
            variant="secondary"
            size="md">
            {t('cancel')}
          </Button>
          <Button onClick={handleConfirmDelete}
            variant="bad"
            size="md">
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageArea>
  );
};

export default ControlTemplatesPage;
